/* eslint-disable @typescript-eslint/no-magic-numbers */

import {HslColor, HsvColor, RgbColor} from '@shared_web/theme/color_models';

const hexToVal = (val: string): number => {
  const num = Number.parseInt(val, 16);
  return Number.isNaN(num) ? 1 : Math.max(0, Math.min(1, num / 255));
};

const valToHex = (val: number): string => {
  return Math.round(Math.max(0, Math.min(1, val)) * 255)
    .toString(16)
    .padStart(2, '0')
    .slice(0, 2);
};

export function hexStringToRgbColor(hex: string): RgbColor {
  // Remove the leading '#' if present
  let value = hex;
  if (hex.startsWith('#')) {
    value = hex.slice(1);
  }

  // Handle 3 char shortcut color format (eg: '#0f0')
  if (value.length === 3) {
    const r = hexToVal(`${value[0]}${value[0]}`);
    const g = hexToVal(`${value[1]}${value[1]}`);
    const b = hexToVal(`${value[2]}${value[2]}`);
    const a = 1;
    return {r, g, b, a};
  }

  // Extract RGB components from 6 first characters
  const r = hexToVal(value.slice(0, 2));
  const g = hexToVal(value.slice(2, 4));
  const b = hexToVal(value.slice(4, 6));

  // Extract alpha component from last characters if present
  const a = value.length === 8 ? hexToVal(value.slice(6, 8)) : 1;

  return {r, g, b, a};
}

export function rgbColorToHslColor(rgb: RgbColor): HslColor {
  const {r, g, b, a} = rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  const d = max - min;
  if (d === 0) {
    return {h: 0, s: 0, l, a};
  }
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) {
    h = (g - b) / d + (g < b ? 6 : 0);
  } else if (max === g) {
    h = (b - r) / d + 2;
  } else {
    h = (r - g) / d + 4;
  }
  h /= 6;

  return {h, s, l, a};
}

export function hexStringToHslColor(hex: string): HslColor {
  const rgb = hexStringToRgbColor(hex);
  const hsl = rgbColorToHslColor(rgb);
  return hsl;
}

export function hslColorToRgbColor(hsl: HslColor): RgbColor {
  const {h, s, l, a} = hsl;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const huePrime = h * 6;
  const secondComponent = chroma * (1 - Math.abs((huePrime % 2) - 1));
  const huePrimeRounded = Math.floor(huePrime);
  let r = 0;
  let g = 0;
  let b = 0;

  if (huePrimeRounded === 0) {
    r = chroma;
    g = secondComponent;
    b = 0;
  } else if (huePrimeRounded === 1) {
    r = secondComponent;
    g = chroma;
    b = 0;
  } else if (huePrimeRounded === 2) {
    r = 0;
    g = chroma;
    b = secondComponent;
  } else if (huePrimeRounded === 3) {
    r = 0;
    g = secondComponent;
    b = chroma;
  } else if (huePrimeRounded === 4) {
    r = secondComponent;
    g = 0;
    b = chroma;
  } else if (huePrimeRounded === 5) {
    r = chroma;
    g = 0;
    b = secondComponent;
  }

  const lightnessAdjustment = l - chroma / 2;
  r += lightnessAdjustment;
  g += lightnessAdjustment;
  b += lightnessAdjustment;

  return {r, g, b, a};
}

export function hsvColorToRgbColor(hsv: HsvColor): RgbColor {
  const hsl = hsvColorToHslColor(hsv);
  const rgb = hslColorToRgbColor(hsl);
  return rgb;
}

export function rgbColorToHexString(rgb: RgbColor): string {
  const {r, g, b, a} = rgb;
  return `#${valToHex(r)}${valToHex(g)}${valToHex(b)}${a === 1 ? '' : valToHex(a)}`;
}

export function hslColorToHexString(hsl: HslColor): string {
  const rgb = hslColorToRgbColor(hsl);
  const hex = rgbColorToHexString(rgb);
  return hex;
}

export function hsvColorToHexString(hsv: HsvColor): string {
  const rgb = hsvColorToRgbColor(hsv);
  const hex = rgbColorToHexString(rgb);
  return hex;
}

export function hslColorToHsvColor(hsl: HslColor): HsvColor {
  const {h, s: s_l, l, a} = hsl;
  const v = l + s_l * Math.min(l, 1 - l);
  const s_v = v === 0 ? 0 : 2 * (1 - l / v);
  return {h, s: s_v, v, a};
}

export function hsvColorToHslColor(hsv: HsvColor): HslColor {
  const {h, s: s_v, v, a} = hsv;
  const l = v * (1 - s_v / 2);
  const s_l = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);
  return {h, s: s_l, l, a};
}
